import { formatMoney } from '@stitch-fix/format';
import type { ReactElement } from 'react';
import { ColorSwatches } from '@stitch-fix/mode-react';
import ProductCard from '@stitch-fix/mode-react/ProductCard';
import type { ShopProductItemFragment } from './getUngatedPaginatedShopCategory';
import { type ShopCategorySwatchVariant, useShopCategoryColorSwatches } from './useShopCategoryColorSwatches';
import { PromoCard, type ShopCategoryPromoCard } from '../PromoCard/PromoCard';
const shouldRenderPromoCard = (promoCard: ShopCategoryPromoCard, productIndex: number, pageSize: number) =>
// Renders the card at its grid position and at that same slot on each later page
promoCard.gridPositions.some(startPosition => {
  const offset = productIndex - startPosition;
  return offset >= 0 && offset % pageSize === 0;
});
const getPreferredMedia = (variant: ShopCategorySwatchVariant, index: number) => {
  const media = variant.media || [];
  let contentName = 'flat_front';
  if (index % 2 === 0) {
    contentName = 'figure_front';
  } else if (index % 5 === 0) {
    contentName = 'figure_left';
  }
  return media.find(m => m.contentName === contentName) ?? media.find(m => m.contentName === 'flat_front');
};
const parseMediaUrl = (src = '') => {
  const [baseUrlRoot, path] = src.split('/upload');
  return {
    baseUrl: baseUrlRoot ? `${baseUrlRoot}/upload` : '',
    mediaPath: path || ''
  };
};
const ShopProductCard = ({
  index,
  shopProduct
}: {
  index: number;
  shopProduct: ShopProductItemFragment;
}) => {
  const {
    colorSwatchesProps,
    displayedAvailability,
    displayedVariant
  } = useShopCategoryColorSwatches({
    shopProduct
  });
  if (!displayedVariant) return null;
  const media = getPreferredMedia(displayedVariant, index);
  const {
    baseUrl,
    mediaPath
  } = parseMediaUrl(media?.originalSrc);
  return <ProductCard heroStageAspectRatio="4:5" sku={displayedVariant.sku} name={shopProduct.name} brand={shopProduct.brand.name} href={displayedVariant.url} promoDiscountLabel={displayedVariant.promoDiscountOffer?.displayLabel.formatted} stickerLabel={displayedAvailability === 'OUT_OF_STOCK' ? {
    text: 'Out of stock',
    variant: 'navy'
  } : undefined} price={displayedVariant.price && formatMoney(displayedVariant.price.value, {
    inCents: true
  })} heroMedia={{
    baseUrl,
    mediaPath,
    alt: `${shopProduct.brand.name} ${shopProduct.name}`.trim(),
    sizes: {
      sm: 320,
      md: 400
    },
    transforms: {
      ar: 1
    }
  }} colorTable={colorSwatchesProps.colors.length > 0 ? {
    type: 'interactive',
    element: <ColorSwatches {...colorSwatchesProps} />
  } : undefined} cardSize="small" />;
};
export const useBuildShopProductItems = ({
  shopProducts,
  promoCards = [],
  pageSize
}: {
  shopProducts: ShopProductItemFragment[];
  promoCards?: ShopCategoryPromoCard[];
  pageSize: number;
}) => {
  const shopCategory = shopProducts.flatMap((shopProduct, index) => {
    const productIndex = index + 1;
    const {
      id,
      variantBySkuId
    } = shopProduct;
    const items: ReactElement[] = [];

    // determine promo cards for this slot
    const activePromos = promoCards.filter(promoCard => shouldRenderPromoCard(promoCard, productIndex, pageSize)).map((promoCard, i) =>
    // eslint-disable-next-line react/no-array-index-key
    <PromoCard key={`promo-${productIndex}-${i}`} {...promoCard} />);
    items.push(...activePromos);

    // add the product card
    items.push(<ShopProductCard key={variantBySkuId?.id || id} index={index} shopProduct={shopProduct} />);
    return items;
  });
  return {
    shopCategory
  };
};